<script lang="ts">
    import type {DocumentAvailability} from 'src/typings';
    import {getDateFormatter, getLocalization} from 'src/core';
    import {pipe} from 'core/utils';
    import {byProperty, anyMatches} from 'shared/utils/array-utils';
    import {inline, prop, strParams} from 'shared/utils/pipes';
    import KpPluralizeText from 'shared/components/kp-pluralize-text/KpPluralizeText.svelte';

    export let availability: DocumentAvailability;
    export let showExemplarsInfo = true;

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();

    const isDownloadable = anyMatches(availability.capabilities, byProperty('name', 'downloadability'));
    const isPalmknihyLoanable = anyMatches(availability.capabilities, byProperty('name', 'palmknihyLoanability'));
    const isPalmknihyAudioLoanable = anyMatches(availability.capabilities, byProperty('name', 'palmknihyAudioLoanability'));
    const isPalmknihyPdfLoanable = anyMatches(availability.capabilities, byProperty('name', 'palmknihyPdfLoanability'));
    const isFlexiBooksLoanable = anyMatches(availability.capabilities, byProperty('name', 'flexibooksLoanability'));
    const isLevnaKnihovnaLoanable = anyMatches(availability.capabilities, byProperty('name', 'levnaknihovnaLoanability'));

    const showAvailabilityInfo = availability.reasonable && (availability.free || availability.reservable || !isDownloadable);
</script>

{#if showAvailabilityInfo}
    <div class="availability-exemplars-info">
        <div class="availability-exemplars-info-loan">
            <div class="availability-exemplars-info-loan-std">
                <!-- ZELENE NEBO CERVENE KOLECKO -->
                {#if availability.freeItemsCount === 0}
                    <img src={'/resources/img/icons/availability_no.gif'} width="14" height="14" loading="lazy" alt={localize(/* @kp-localization loan.NelzeVypujcit */ 'loan.NelzeVypujcit')}/>
                {:else if availability.freeItemsCount > 0}
                    <img src={'/resources/img/icons/availability_yes.gif'} width="14" height="14" loading="lazy" alt={localize(/* @kp-localization loan.Available */ 'loan.Available')}/>
                {/if}

                <!-- TEXT x k vypujceni, y prezencne z z exmplaru NEBO zadne exemplare -->
                {#if availability.obtainableItemsCount === 0}
                    <span class="availability-no-exemplars-text">
                        {localize(/* @kp-localization loan.DokumentNemaExemplare */ 'loan.DokumentNemaExemplare')}
                    </span>
                {:else if availability.obtainableItemsCount > 0}
                    <span class="availability-obtainable-exemplars-text">
                        <span class="sr-only">{localize(/* @kp-localization commons.Exemplare */ 'commons.Exemplare')}: </span>
                        {pipe(localize(/* @kp-localization loan.XKVypujceniAYPrezencneZZ */ 'loan.XKVypujceniAYPrezencneZZ'), strParams(availability.freeTakeawayItemsCount.toString(), availability.freeVisitableItemsCount.toString(), availability.obtainableItemsCount.toString()))}
                    </span>
                {/if}
            </div>

            {#if isPalmknihyLoanable || isPalmknihyAudioLoanable || isPalmknihyPdfLoanable || isFlexiBooksLoanable || isLevnaKnihovnaLoanable}
                <div class="availability-exemplars-info-loan-eloan">
                    <img src={'/resources/img/icons/ebook.png'} width="18" height="18" loading="lazy" alt={localize(/* @kp-localization loan.eVypujcka */ 'loan.eVypujcka')}/>
                    {localize(/* @kp-localization loan.eVypujcka */ 'loan.eVypujcka')}
                </div>
            {/if}
        </div>

        {#if showExemplarsInfo}
            <!-- SEZNAM BUDOV S EXEMPLARI, zobrazeno pomoci custom stylu v KFBZ -->
            <div class="availability-exemplars-departments-info">
                {#if availability.freeItemsDepartments.length > 0}
                    <span class="availability-exemplars-departments-info-free">
                        <KpPluralizeText
                            count={availability.freeItemsDepartments.length}
                            oneCountText={localize(/* @kp-localization loan.VolnyNaBudove */ 'loan.VolnyNaBudove')}
                            moreText={localize(/* @kp-localization loan.VolnyNaBudovach */ 'loan.VolnyNaBudovach')}
                        />
                        {pipe(availability.freeItemsDepartments, prop('text'), inline())}
                    </span>
                {/if}

                {#if availability.freeItemsDepartments.length > 0 && availability.reservableItemsDepartments.length > 0}·{/if}

                {#if availability.reservableItemsDepartments.length > 0}
                    <span class="availability-exemplars-departments-info-reservable">
                        <KpPluralizeText
                            count={availability.reservationsCount}
                            zeroCountText={localize(/* @kp-localization loan.ForReservationOn */ 'loan.ForReservationOn')}
                            moreText={localize(/* @kp-localization loan.ForReservationOfAllXOn */ 'loan.ForReservationOfAllXOn')}
                        />
                        <span class="availability-exemplars-departments-info-reservable-deps">
                            {pipe(availability.reservableItemsDepartments, prop('text'), inline())}
                        </span>
                    </span>
                {/if}
            </div>
        {/if}

        {#if availability.lastUserLoanDate}
            <div class="last-user-loan-date text-muted small">
                {pipe(localize(/* @kp-localization loan.LentX */ 'loan.LentX'), strParams(pipe(availability.lastUserLoanDate, dateFormatter('d.M.yyyy'))))}
            </div>
        {/if}

    </div>
{/if}

<style lang="less">
    .availability-exemplars-info-loan {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .availability-exemplars-departments-info {
        color: gray;
    }
</style>